/* One flat icon set for the product concepts.

   These replaced a set of glossy 3D PNG illustrations (`/assets/icons/*.png`,
   classes `.cci3d` / `.nd-3d`) that sat inside a site drawn entirely from
   2-tone line icons — the mismatch was visible the moment a nav dropdown opened
   next to any other icon on the page.

   House rules, so nothing drifts again:
     · 24×24 viewBox
     · fill="none", stroke="currentColor" — colour comes from CSS, one icon
       works on light and dark and in any accent
     · strokeWidth 2.2, round caps and joins
     · no gradients, no highlights, no drop shadows */

const S = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const PATHS = {
  /* productivity — a rising bar chart */
  productivity: <><path {...S} d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,

  /* monitoring — an eye, the privacy-first watching metaphor */
  monitoring: <><path {...S} d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" /><circle {...S} cx="12" cy="12" r="3" /></>,

  /* time — a clock */
  time: <><circle {...S} cx="12" cy="12" r="9" /><path {...S} d="M12 7v5l3.5 2" /></>,

  /* hrms — a person and their record */
  hrms: <><circle {...S} cx="9" cy="8.5" r="3.5" /><path {...S} d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path {...S} d="M17 8h4M17 12h4M17 16h4" /></>,

  /* crm — a pipeline funnel */
  crm: <><path {...S} d="M3 5h18l-7 8v6l-4 2v-8L3 5Z" /></>,

  /* email — an envelope */
  email: <><rect {...S} x="2.5" y="5" width="19" height="14" rx="2.5" /><path {...S} d="m3.5 7.5 8.5 6 8.5-6" /></>,

  /* goals — a target */
  goals: <><circle {...S} cx="12" cy="12" r="8.5" /><circle {...S} cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1.6" fill="currentColor" /></>,

  /* analytics — the brain/pattern mark, drawn as a node cluster */
  analytics: <><circle {...S} cx="12" cy="12" r="3" /><circle {...S} cx="5" cy="6" r="2.2" /><circle {...S} cx="19" cy="6" r="2.2" /><circle {...S} cx="5" cy="18" r="2.2" /><circle {...S} cx="19" cy="18" r="2.2" /><path {...S} d="m6.8 7.4 3 3m4.4 0 3-3m-3 6.2 3 3m-7.4-3-3 3" /></>,

  /* chat — a speech bubble */
  chat: <><path {...S} d="M21 12a8 8 0 0 1-8 8H4l2-3a8 8 0 1 1 15-5Z" /><path {...S} d="M9 11h7M9 15h4" /></>,

  /* org — a hierarchy */
  org: <><rect {...S} x="9" y="3" width="6" height="5" rx="1.5" /><rect {...S} x="2.5" y="16" width="6" height="5" rx="1.5" /><rect {...S} x="15.5" y="16" width="6" height="5" rx="1.5" /><path {...S} d="M12 8v4M5.5 16v-2h13v2" /></>,

  /* docs — a written page */
  docs: <><path {...S} d="M6 3h8l4 4v14H6z" /><path {...S} d="M14 3v4h4" /><path {...S} d="M9 12h6M9 16h4" /></>,

  /* security — a shield */
  security: <><path {...S} d="M12 3 20 6v6c0 5-3.4 8-8 9-4.6-1-8-4-8-9V6l8-3Z" /><path {...S} d="m9 12 2 2 4-4" /></>,
};

export default function ProductIcon({ name, size = 24, className = '', ...rest }) {
  const path = PATHS[name] || PATHS.analytics;
  return (
    <svg
      className={`picon ${className}`.trim()}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {path}
    </svg>
  );
}
